import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TooltipContentProps, TooltipValueType } from 'recharts';
import { FiAlertCircle, FiArrowDownRight, FiArrowUpRight, FiClock, FiCreditCard, FiEye, FiFileText, FiImage, FiRefreshCw, FiShoppingCart, FiUserPlus, FiUsers, FiZap } from 'react-icons/fi';
import { apiRequest } from '../utils/auth';
import type { FinancialAnalytics, MoneyRow } from '../financial/types';
import { formatMoney } from '../financial/format';
import { reportCurrency } from '../financial/currency';
import type { DailyDashboardMetric, FunnelKey, FunnelStep, PeriodDashboardResponse } from '../types/periodDashboard';
import {
  formatDate,
  formatMoment,
  formatNumber,
  formatRange,
  formatRatio,
  hktToday,
  metricChange,
  previousRange,
  rangeDays,
  recentDays,
  validateRange,
} from '../utils/periodDashboard';
import './PeriodDashboardPage.css';

type Metric = 'images' | 'newUsers' | 'purchases';
type ChartPoint = DailyDashboardMetric & { currentValue: number; previousValue: number };

const METRICS: { key: Metric; label: string; shortLabel: string }[] = [
  { key: 'images', label: 'Images generated', shortLabel: 'Images' },
  { key: 'newUsers', label: 'New users', shortLabel: 'New users' },
  { key: 'purchases', label: 'Purchases', shortLabel: 'Purchases' },
];

const FUNNEL_LABELS: Record<FunnelKey, string> = {
  signup: 'Signed up',
  generation: 'First generation',
  paywall: 'Viewed paywall',
  checkout: 'Started checkout',
  purchase: 'First paid purchase',
};
const FUNNEL_ICONS = {
  signup: FiUserPlus,
  generation: FiFileText,
  paywall: FiEye,
  checkout: FiShoppingCart,
  purchase: FiCreditCard,
};
const KPI_ICONS = [FiImage, FiUserPlus, FiUsers, FiShoppingCart, FiZap];

function conciseNote(value: string): string {
  return value.replace(/\bHKT\b/g, 'local time');
}

function numberValue(value: number | null | undefined): string {
  return typeof value === 'number' ? formatNumber(value) : value === null ? 'Unavailable' : 'Not reported';
}

function rateValue(value: number | null | undefined): string {
  return typeof value === 'number' ? formatRatio(value) : value === null ? 'Unavailable' : 'Not reported';
}

function changeClass(current: number | null, previous: number | null, points = false): string {
  const change = metricChange(current, previous, points);
  if (change.kind === 'unavailable') return 'is-muted';
  return change.positive ? 'is-positive' : 'is-negative';
}

function MetricCard({
  index,
  label,
  value,
  previous,
  percentagePoints = false,
  missingReason,
}: {
  index: number;
  label: string;
  value: number | null | undefined;
  previous: number | null | undefined;
  percentagePoints?: boolean;
  missingReason?: string | null;
}) {
  const current = value ?? null;
  const before = previous ?? null;
  const change = metricChange(current, before, percentagePoints);
  const formatted = percentagePoints ? rateValue(value) : numberValue(value);
  const formattedPrevious = percentagePoints ? rateValue(previous) : numberValue(previous);
  const Icon = KPI_ICONS[index - 1];

  return (
    <article className={`period-kpi period-kpi--${index}`} aria-label={`${label}: ${formatted}; ${change.label} versus previous period`}>
      <div className="period-kpi-label"><Icon aria-hidden="true" />{label}</div>
      <div className="period-kpi-value">{formatted}</div>
      <span className={`period-delta ${changeClass(current, before, percentagePoints)}`}>
        {change.kind === 'change' && (change.positive ? <FiArrowUpRight aria-hidden="true" /> : <FiArrowDownRight aria-hidden="true" />)}
        {change.label}
      </span>
      <span className="period-visually-hidden">Previous {formattedPrevious}</span>
      {missingReason && (value === null || previous === null) ? <p className="period-kpi-reason">{missingReason}</p> : null}
    </article>
  );
}

function ChartTooltip({ active, payload }: TooltipContentProps<TooltipValueType>) {
  if (!active || !payload?.length) return null;
  const point = payload[0]?.payload as ChartPoint | undefined;
  if (!point) return null;
  return (
    <div className="period-chart-tooltip">
      <strong>{formatDate(point.date, true)}</strong>
      <span>Selected: {formatNumber(point.currentValue)}</span>
      <span>{formatDate(point.comparisonDate, true)}: {formatNumber(point.previousValue)}</span>
    </div>
  );
}

function Funnel({ steps, status, reason, notes }: {
  steps: FunnelStep[];
  status: PeriodDashboardResponse['availability']['funnel']['status'];
  reason: string | null;
  notes: string[];
}) {
  const observedCounts = steps.map((step) => step.count).filter((count): count is number => typeof count === 'number');
  const maxCount = observedCounts.length ? Math.max(...observedCounts, 1) : 1;
  const canDiagnose = status === 'observed' && steps.every((step) => step.count !== null);
  const drops = canDiagnose
    ? steps.slice(1).map((step, index) => {
      const previous = steps[index].count as number;
      const current = step.count as number;
      return previous > 0 ? 1 - current / previous : 0;
    })
    : [];
  const biggestDropIndex = drops.length ? drops.indexOf(Math.max(...drops)) : -1;
  const checkout = steps.find((step) => step.key === 'checkout')?.count ?? null;
  const purchase = steps.find((step) => step.key === 'purchase')?.count ?? null;
  const checkoutRate = status === 'observed' && checkout !== null && purchase !== null && checkout > 0
    ? formatRatio(purchase / checkout)
    : null;

  return (
    <section className="period-panel period-funnel-panel" aria-labelledby="funnel-heading">
      <div className="period-panel-heading">
        <div>
          <h2 id="funnel-heading">Conversion</h2>
        </div>
        {status !== 'observed' ? <span className={`period-status status-${status}`}>{status === 'partial' ? 'Partial data' : 'Unavailable'}</span> : null}
      </div>
      {status !== 'observed' ? (
        <div className="period-tracking-note" role="note">
          <FiAlertCircle aria-hidden="true" />
          <span>Some steps are unavailable.</span>
        </div>
      ) : null}
      <div className="period-funnel" aria-label="New-user conversion funnel">
        {steps.map((step, index) => {
          const Icon = FUNNEL_ICONS[step.key];
          const before = index ? steps[index - 1].count : null;
          const drop = canDiagnose && typeof before === 'number' && before > 0 && step.count !== null
            ? 1 - step.count / before
            : null;
          return (
            <div className="period-funnel-group" key={step.key}>
              <div className="period-funnel-row">
                <span className="period-funnel-label"><Icon aria-hidden="true" />{FUNNEL_LABELS[step.key]}</span>
                <span className="period-funnel-value">{numberValue(step.count)}{step.count !== null ? <small>{((step.count / maxCount) * 100).toFixed(1)}%</small> : null}</span>
                <span className="period-funnel-track" aria-hidden="true"><span style={{ width: `${step.count === null ? 0 : (step.count / maxCount) * 100}%` }} /></span>
              </div>
              {drop !== null ? <span className="period-visually-hidden">{(drop * 100).toFixed(1)}% drop from previous step</span> : null}
            </div>
          );
        })}
      </div>
      {checkoutRate ? <div className="period-funnel-result"><span>Checkout → first purchase</span><strong>{checkoutRate}</strong></div> : null}
      <details className="period-method">
        <summary>Funnel details</summary>
        {reason ? <p>{conciseNote(reason)}</p> : null}
        {canDiagnose && biggestDropIndex >= 0 ? <p>Largest drop: {steps[biggestDropIndex].label} → {steps[biggestDropIndex + 1].label} · {(drops[biggestDropIndex] * 100).toFixed(1)}%</p> : null}
        <p>First paid purchase counts users in this signup cohort; the KPI above counts paid transactions across all users. Generation creation time is the completion proxy for this funnel.</p>
        {notes.map((note) => <p key={note}>{conciseNote(note)}</p>)}
      </details>
    </section>
  );
}

function parseInitialRange(search: URLSearchParams): { startDate: string; endDate: string } {
  const candidate = { startDate: search.get('startDate') || '', endDate: search.get('endDate') || '' };
  return validateRange(candidate).valid ? candidate : recentDays(30);
}

function DashboardFinancialCards({ range, currency, setCurrency }: {
  range: { startDate: string; endDate: string };
  currency: string;
  setCurrency: (currency: string) => void;
}) {
  const [data, setData] = useState<FinancialAnalytics | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(0);
  const requestId = useRef(0);

  useEffect(() => {
    const controller = new AbortController();
    const sequence = ++requestId.current;
    setLoading(true);
    setError(null);
    setData(null);
    const query = new URLSearchParams({ startDate: range.startDate, endDate: range.endDate, environment: 'production', internal: 'exclude' });
    apiRequest<FinancialAnalytics>(`/api/admin/revenue/analytics?${query}`, { signal: controller.signal })
      .then((response) => {
        if (response.success === false) throw new Error('The financial service did not return a successful response.');
        if (sequence === requestId.current && !controller.signal.aborted) setData(response);
      })
      .catch((requestError: unknown) => {
        if (sequence === requestId.current && !controller.signal.aborted) {
          setError(requestError instanceof Error ? requestError.message : 'Financial data could not be loaded.');
        }
      })
      .finally(() => {
        if (sequence === requestId.current && !controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [range.startDate, range.endDate, refresh]);

  const currencies = [...new Set([...(currency ? [currency] : []), ...(data?.current.map((item) => item.currency) || []), ...(data?.previous.map((item) => item.currency) || [])])];
  const selectedCurrency = currency || (currencies.includes('HKD') ? 'HKD' : currencies[0] || '');
  const row: MoneyRow | undefined = data?.current.find((item) => item.currency === selectedCurrency);
  const prior: MoneyRow | undefined = data?.previous.find((item) => item.currency === selectedCurrency);
  const link = `/analytics/financial?${new URLSearchParams({ startDate: range.startDate, endDate: range.endDate, environment: 'production', internal: 'exclude', ...(selectedCurrency ? { currency: selectedCurrency } : {}) })}`;

  return (
    <section className="period-financial period-panel" aria-labelledby="dashboard-financial-heading">
      <div className="period-panel-heading period-financial-heading">
        <h2 id="dashboard-financial-heading">Finance</h2>
        <div className="period-financial-controls">
          <label>
            <span className="period-visually-hidden">Currency</span>
            <select aria-label="Currency" value={selectedCurrency} onChange={(event) => setCurrency(event.target.value)}>
              {!currencies.length ? <option value="">Currency</option> : null}
              {currencies.map((item) => <option key={item} value={item}>{item === 'UNKNOWN' ? 'Unknown currency' : item}</option>)}
            </select>
          </label>
          <Link to={link}>View details</Link>
        </div>
      </div>
      {error ? (
        <div className="period-financial-message is-error" role="alert">
          <span>Financial data unavailable.</span>
          <button type="button" onClick={() => setRefresh((value) => value + 1)}>Retry</button>
        </div>
      ) : loading ? (
        <div className="period-financial-message" role="status">Loading finance…</div>
      ) : row && selectedCurrency !== 'UNKNOWN' ? (
        <div className="period-financial-values">
          <FinancialCard label="Gross collections" value={row.grossCollections} prior={prior?.grossCollections ?? null} currency={row.currency} coverage={row.coverage.grossKnown} priorCoverage={prior?.coverage.grossKnown ?? null} priorTotal={prior?.coverage.total ?? null} total={row.coverage.total} to={`${link}&view=transactions`} />
          <FinancialCard label="After refunds" value={row.collectionsAfterRefunds} prior={prior?.collectionsAfterRefunds ?? null} currency={row.currency} coverage={row.coverage.adjustedKnown} priorCoverage={prior?.coverage.adjustedKnown ?? null} priorTotal={prior?.coverage.total ?? null} total={row.coverage.total} to={`${link}&view=transactions`} />
          <FinancialCard label="Recorded refunds" value={row.refunds} prior={prior?.refunds ?? null} currency={row.currency} coverage={row.coverage.refundsKnown} priorCoverage={prior?.coverage.refundsKnown ?? null} priorTotal={prior?.coverage.total ?? null} total={row.coverage.total} to={`${link}&view=transactions`} />
        </div>
      ) : (
        <div className="period-financial-message" role="status">
          {selectedCurrency === 'UNKNOWN' ? 'Unknown-currency records have no monetary total.' : selectedCurrency ? `No recorded payments in ${selectedCurrency}.` : 'No recorded payments for this period.'}
        </div>
      )}
      <details className="period-method period-financial-method">
        <summary>Finance definitions</summary>
        <p>Provider-confirmed payments, one currency at a time. Amount coverage is shown for each figure; incomplete evidence is marked Partial.</p>
        <p>Refunds are cumulative refunds on selected payments as of this report, not cash flow by refund date.</p>
        {prior && selectedCurrency !== 'UNKNOWN' ? <p>Previous period: gross {formatMoney(prior.grossCollections, selectedCurrency)} · after refunds {formatMoney(prior.collectionsAfterRefunds, selectedCurrency)} · refunds {formatMoney(prior.refunds, selectedCurrency)}.</p> : null}
      </details>
    </section>
  );
}

function FinancialCard({ label, value, prior, currency, coverage, priorCoverage, priorTotal, total, to }: {
  label: string;
  value: string | null;
  prior: string | null;
  currency: string;
  coverage: number;
  priorCoverage: number | null;
  priorTotal: number | null;
  total: number;
  to: string;
}) {
  const partial = coverage < total;
  return (
    <Link to={to} className="period-financial-item">
      <span>{label}</span>
      <strong>{formatMoney(value, currency)}</strong>
      <small className={total === 0 ? 'is-unavailable' : partial ? 'is-partial' : 'is-complete'}>
        {total === 0 ? 'No evidence' : partial ? `Partial · ${formatNumber(coverage)} / ${formatNumber(total)}` : `Evidence · ${formatNumber(coverage)} / ${formatNumber(total)}`}
      </small>
      <span className="period-visually-hidden">Previous {formatMoney(prior, currency)}; previous evidence {priorCoverage === null || priorTotal === null ? 'unavailable' : `${formatNumber(priorCoverage)} of ${formatNumber(priorTotal)}`}.</span>
    </Link>
  );
}

export default function PeriodDashboardPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [range, setRange] = useState(() => parseInitialRange(searchParams));
  const [draft, setDraft] = useState(range);
  const [showCustom, setShowCustom] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [dashboard, setDashboard] = useState<PeriodDashboardResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [metric, setMetric] = useState<Metric>('images');
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const requestId = useRef(0);
  const abortRef = useRef<AbortController | null>(null);

  const updateRange = useCallback((nextRange: { startDate: string; endDate: string }) => {
    // Clear in the interaction itself, before the effect gets a chance to fetch.
    setDashboard(null);
    setRange(nextRange);
    setDraft(nextRange);
    const next = new URLSearchParams(searchParams);
    next.set('startDate', nextRange.startDate); next.set('endDate', nextRange.endDate);
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    const sequence = ++requestId.current;
    // Never associate an old response with a newly selected date range.
    setDashboard(null);
    setLoading(true);
    setError(null);
    const query = new URLSearchParams(range).toString();

    apiRequest<PeriodDashboardResponse>(`/api/admin/period-dashboard?${query}`, { signal: controller.signal })
      .then((response) => {
        if (sequence !== requestId.current || controller.signal.aborted) return;
        if (response.success === false) throw new Error('The dashboard service did not return a successful response.');
        setDashboard(response);
      })
      .catch((requestError: unknown) => {
        if (sequence !== requestId.current || controller.signal.aborted) return;
        setError(requestError instanceof Error ? requestError.message : 'Unable to load the dashboard.');
      })
      .finally(() => {
        if (sequence === requestId.current && !controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [range]);

  const comparison = dashboard?.comparison || previousRange(range);
  const activeMetric = METRICS.find((item) => item.key === metric) || METRICS[0];
  const chartData = useMemo<ChartPoint[]>(() => (dashboard?.daily || []).map((row) => ({
    ...row,
    currentValue: metric === 'images' ? row.images : metric === 'newUsers' ? row.newUsers : row.purchases,
    previousValue: metric === 'images' ? row.previousImages : metric === 'newUsers' ? row.previousNewUsers : row.previousPurchases,
  })), [dashboard, metric]);
  const focusedPoint = focusedIndex === null ? null : chartData[focusedIndex] || null;

  const selectRecentPeriod = (days: number) => {
    const next = recentDays(days);
    setValidationError(null);
    setShowCustom(false);
    setFocusedIndex(null);
    updateRange(next);
  };

  const submitCustomRange = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const result = validateRange(draft);
    if (!result.valid) {
      setValidationError((result.error || 'Choose a valid date range.').replace('HKT ', ''));
      return;
    }
    setValidationError(null);
    setShowCustom(false);
    setFocusedIndex(null);
    updateRange(draft);
  };

  const onChartKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!chartData.length) return;
    if (event.key === 'Escape') {
      setFocusedIndex(null);
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    setFocusedIndex((current) => {
      const index = current === null ? 0 : current + (event.key === 'ArrowRight' ? 1 : -1);
      return Math.min(Math.max(index, 0), chartData.length - 1);
    });
  };

  const selectedDays = rangeDays(range);
  const isPresetRange = [1, 7, 30, 90].some((days) => selectedDays === days && range.endDate === hktToday());
  const customRange = !isPresetRange;
  const availabilityReason = dashboard?.availability.activeUsers.reason;
  const financialCurrency = searchParams.has('currency') ? reportCurrency(searchParams) || '' : '';
  const setFinancialCurrency = useCallback((currency: string) => { const next = new URLSearchParams(searchParams); next.set('currency', currency); setSearchParams(next); }, [searchParams, setSearchParams]);

  const onPeriodChange = (value: string) => {
    if (value === 'custom') {
      setValidationError(null);
      setShowCustom(true);
      return;
    }
    selectRecentPeriod(Number(value));
  };

  return (
    <div className="page period-dashboard">
      <header className="period-header">
        <h1>Dashboard</h1>
        <select className="period-period-select" aria-label="Reporting period" value={showCustom || customRange ? 'custom' : String(selectedDays)} onChange={(event) => onPeriodChange(event.target.value)}>
          <option value="1">Today</option>
          <option value="7">7 days</option>
          <option value="30">30 days</option>
          <option value="90">90 days</option>
          <option value="custom">Custom</option>
        </select>
      </header>

      <div className="period-context">
        {customRange ? <button type="button" className="period-custom-trigger" onClick={() => setShowCustom((visible) => !visible)} aria-expanded={showCustom} aria-controls="period-custom-range">{formatRange(range)}</button> : null}
        <span>vs previous period</span>
        {range.endDate === hktToday() ? <span className="period-today-note">Today incomplete</span> : null}
        <span className="period-visually-hidden">Current: {formatRange(range)}. Previous: {formatRange(comparison)}. Internal and test accounts excluded.</span>
      </div>

      {showCustom ? (
        <form id="period-custom-range" className="period-date-form" onSubmit={submitCustomRange}>
          <label>Start date<input type="date" value={draft.startDate} max={hktToday()} onChange={(event) => setDraft((value) => ({ ...value, startDate: event.target.value }))} required /></label>
          <label>End date<input type="date" value={draft.endDate} max={hktToday()} onChange={(event) => setDraft((value) => ({ ...value, endDate: event.target.value }))} required /></label>
          <button type="submit">Apply</button>
          <button type="button" className="period-date-cancel" onClick={() => { setShowCustom(false); setDraft(range); setValidationError(null); }}>Cancel</button>
          {validationError ? <span className="period-date-error" role="alert">{validationError}</span> : null}
        </form>
      ) : null}

      {error ? (
        <section className="period-error" role="alert"><FiAlertCircle /><div><strong>Dashboard unavailable</strong><span>{error}</span></div><button type="button" onClick={() => updateRange({ ...range })}><FiRefreshCw /> Retry</button></section>
      ) : null}

      {dashboard ? (
        <>
          <section className="period-kpis" aria-label="Period performance" aria-live="polite">
            <MetricCard index={1} label="Images" value={dashboard.current.images} previous={dashboard.previous.images} />
            <MetricCard index={2} label="New users" value={dashboard.current.newUsers} previous={dashboard.previous.newUsers} />
            <MetricCard index={3} label="Active users" value={dashboard.availability.activeUsers.current ? dashboard.current.activeUsers : null} previous={dashboard.availability.activeUsers.previous ? dashboard.previous.activeUsers : null} missingReason={availabilityReason ? conciseNote(availabilityReason) : null} />
            <MetricCard index={4} label="Purchases" value={dashboard.current.purchases} previous={dashboard.previous.purchases} />
            <MetricCard index={5} label="Activation" value={dashboard.current.activationRate} previous={dashboard.previous.activationRate} percentagePoints />
          </section>

          <div className="period-analysis">
          <section className="period-panel period-chart-panel" aria-labelledby="daily-performance-heading">
            <div className="period-panel-heading">
              <h2 id="daily-performance-heading">Daily trend</h2>
              <div className="period-chart-controls">
                <select aria-label="Chart metric" value={metric} onChange={(event) => { setMetric(event.target.value as Metric); setFocusedIndex(null); }}>
                  {METRICS.map((item) => <option key={item.key} value={item.key}>{item.shortLabel}</option>)}
                </select>
                <div className="period-chart-legend" aria-hidden="true"><span><i className="period-current-line" />Selected period</span><span><i className="period-previous-line" />Previous period</span></div>
              </div>
            </div>
            {chartData.length ? (
              <div className="period-chart-focus" tabIndex={0} onKeyDown={onChartKeyDown} onBlur={() => setFocusedIndex(null)} aria-label={`Daily ${activeMetric.label} chart. Use left and right arrow keys to inspect a day.`}>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={chartData} margin={{ top: 20, right: 12, left: 0, bottom: 0 }}>
                    <defs><linearGradient id="period-dashboard-area" x1="0" x2="0" y1="0" y2="1"><stop offset="0%" stopColor="var(--ui-chart-current)" stopOpacity={0.16} /><stop offset="100%" stopColor="var(--ui-chart-current)" stopOpacity={0} /></linearGradient></defs>
                    <CartesianGrid stroke="var(--ui-chart-grid)" vertical={false} />
                    <XAxis dataKey="date" tickFormatter={(value: string) => formatDate(value)} minTickGap={32} tick={{ fill: 'var(--color-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis width={42} allowDecimals={false} tick={{ fill: 'var(--color-muted)', fontSize: 11 }} axisLine={false} tickLine={false} />
                    <Tooltip content={ChartTooltip} />
                    <Area type="monotone" dataKey="previousValue" name="Previous" stroke="var(--ui-chart-previous)" strokeDasharray="5 5" fill="none" strokeWidth={2} isAnimationActive={false} />
                    <Area type="monotone" dataKey="currentValue" name="Selected" stroke="var(--ui-chart-current)" fill="url(#period-dashboard-area)" strokeWidth={2} isAnimationActive={false} />
                    {focusedPoint ? <ReferenceLine x={focusedPoint.date} stroke="var(--ui-chart-current)" strokeDasharray="3 4" /> : null}
                  </AreaChart>
                </ResponsiveContainer>
                {focusedPoint ? <div className="period-keyboard-tooltip" role="status"><strong>{formatDate(focusedPoint.date, true)}</strong><span>Selected: {formatNumber(focusedPoint.currentValue)}</span><span>{formatDate(focusedPoint.comparisonDate, true)}: {formatNumber(focusedPoint.previousValue)}</span></div> : null}
              </div>
            ) : <div className="period-empty"><FiClock /> No daily performance records were returned for this period.</div>}
            <details className="period-method"><summary>Chart details</summary><p>Solid line: selected period. Dashed line: previous period, aligned by day. Hover or use arrow keys for both dates.</p></details>
          </section>
            <Funnel steps={dashboard.funnel.steps} status={dashboard.availability.funnel.status} reason={dashboard.availability.funnel.reason} notes={dashboard.funnel.notes} />
          </div>

          <div className="period-bottom-grid">
            <DashboardFinancialCards key={`${range.startDate}:${range.endDate}`} range={range} currency={financialCurrency} setCurrency={setFinancialCurrency} />
            <section className="period-snapshot period-panel" aria-labelledby="operations-heading">
              <div className="period-snapshot-title"><h2 id="operations-heading">Operations</h2></div>
              <div className="period-snapshot-values">
                <Link to="/payouts" className="period-snapshot-item"><span>Pending payouts</span><strong>{formatNumber(dashboard.snapshot.pendingPayouts)}</strong></Link>
                <Link to="/extensions" className="period-snapshot-item"><span>Suspended lenses</span><strong>{formatNumber(dashboard.snapshot.suspendedLenses)}</strong></Link>
                <Link to="/waitlist" className="period-snapshot-item"><span>Waitlist review</span><strong>{formatNumber(dashboard.snapshot.pendingWaitlist)}</strong></Link>
              </div>
            </section>
          </div>
          <details className="period-data-notes"><summary>Definitions &amp; data notes</summary>
            <p>Activation: new users with a first generation record created in this period. Generation creation time is the completion proxy.</p>
            <p>Active users are distinct signed-in users. Purchases are production transactions with a recorded completion time; mobile renewals may be incomplete.</p>
            <p>Internal and test accounts are excluded. Operations snapshot: {formatMoment(dashboard.snapshot.asOf)}, independent of the selected period.</p>
            <p><Link to="/dashboard/legacy">Other reports</Link></p>
            {dashboard.notes.map((note) => <p key={note}>{conciseNote(note)}</p>)}
          </details>
        </>
      ) : loading ? <div className="period-loading" aria-live="polite"><FiRefreshCw /> Loading period dashboard…</div> : null}
      {!dashboard ? <DashboardFinancialCards key={`${range.startDate}:${range.endDate}`} range={range} currency={financialCurrency} setCurrency={setFinancialCurrency} /> : null}
    </div>
  );
}
